/**
 * 動画の画質一覧取得と、未生成画質のオンデマンド変換を管理するフック
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { getEpisodeQualities, type VideoQualityOption } from '@/api/api'
import { prepareVideoVariant } from '@/lib/video-variant'

export type Conversion = {
  quality: string
  progress: number
}

export const useVideoQuality = (episodeId: number | null) => {
  const [options, setOptions] = useState<VideoQualityOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [conversion, setConversion] = useState<Conversion | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const refresh = useCallback(async () => {
    if (episodeId === null) return
    setIsLoading(true)
    try {
      const res = await getEpisodeQualities(episodeId)
      if (isMountedRef.current) setOptions(res.options)
    } catch {
      // 動画以外のエピソードや取得失敗時は画質選択を出さない
      if (isMountedRef.current) setOptions([])
    } finally {
      if (isMountedRef.current) setIsLoading(false)
    }
  }, [episodeId])

  useEffect(() => {
    refresh()
  }, [refresh])

  /**
   * 指定画質を利用可能にする。
   * 未生成なら変換を依頼し、完了までポーリングする。
   */
  const ensureQuality = useCallback(
    async (quality: string): Promise<boolean> => {
      if (episodeId === null || quality === 'original') return true

      const option = options.find((o) => o.quality === quality)
      if (option?.status === 'ready') return true

      // 多重に呼ばれた場合は古いポーリングを打ち切る
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const available = await prepareVideoVariant(episodeId, quality, {
        onProgress: (progress) => {
          if (!controller.signal.aborted) setConversion({ progress, quality })
        },
        signal: controller.signal,
      })

      if (controller.signal.aborted || !isMountedRef.current) return false
      abortRef.current = null
      setConversion(null)
      if (available) await refresh()
      return available
    },
    [episodeId, options, refresh],
  )

  const cancelPolling = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setConversion(null)
  }, [])

  return { cancelPolling, conversion, ensureQuality, isLoading, options, refresh }
}
